import {EventEmitter, Injectable, OnDestroy} from "@angular/core";
import {TimeSeriesPerformanceStoreService} from "../time-series-performance-store.service";
import {Subscription} from "rxjs";
import {ChartData} from "chart.js/auto";
import {CombinedOrderElementDto} from "../../models/core/combined-order-element-dto";
import {ColorMapService} from "../../../common/colors/color-map.service";
import {LinkedDtoRepository} from "../../../common/repositories/linked-dto-repository";
import {TimeRepository} from "../../repositories/core/time/time-repository";
import {OrderRepository} from "../../repositories/core/orders/order-repository";
import {CategoryRevenueRepository} from "../../repositories/derived/category-revenue/category-revenue-repository";

@Injectable()
export class TimeseriesBarChartStore implements OnDestroy {
  subscription: Subscription = new Subscription()
  isRegistered: boolean = false

  relativeBarChartDataset!: ChartData
  absoluteBarChartDataset!: ChartData

  // todo implement logic
  isBarChartTrendEnabled: boolean = false

  isDataInterpolationActive = true;


  updateNotifier: EventEmitter<any> = new EventEmitter<any>()

  constructor(private timeSeriesPerformanceStoreService: TimeSeriesPerformanceStoreService,
              private timeRepository: TimeRepository,
              private orderRepository: OrderRepository,
              private categoryRevenueRepository: CategoryRevenueRepository,
              private colorMapService: ColorMapService,
              // Todo: use a generic type
              private linkedDtoRepository: LinkedDtoRepository<any>,
  ) {
    this.register()
  }

  register() {
    if (this.isRegistered)
      return
    this.isRegistered = true
    this.subscription.add(this.timeSeriesPerformanceStoreService.updateNotifier.subscribe(() => {
        this.update()
      }
    ))

  }

  getAbsoluteBarChartDataset(): ChartData {
    return this.absoluteBarChartDataset
  }

  getRelativeBarChartDataset(): ChartData {
    return this.relativeBarChartDataset
  }

  update() {
    this.processBarChartDataset("bookingType", this.timeSeriesPerformanceStoreService.revenueFn)
    this.updateNotifier.emit()
  }

  processBarChartDataset(categoryKey: keyof CombinedOrderElementDto, revenueFn: (combinedOrderElementDto: CombinedOrderElementDto) => number) {
    this.absoluteBarChartDataset = this.processTimeSeriesAbsoluteRevenue(categoryKey, revenueFn)
    this.relativeBarChartDataset = this.processTimeSeriesRelativeRevenue(categoryKey, revenueFn)
    this.relativeBarChartDataset = this.isDataInterpolationActive ? this.interpolateData(this.relativeBarChartDataset) : this.removeTensionProperty(this.relativeBarChartDataset);
  }

  processTimeSeriesRelativeRevenue(categoryKey: keyof CombinedOrderElementDto, revenueFn: (combinedOrderElementDto: CombinedOrderElementDto) => number): ChartData {
    // Todo: calculate only once
    let barChartDataset = this.processTimeSeriesAbsoluteRevenue(categoryKey, revenueFn)
    for (let i = 0; i < barChartDataset.labels!.length; i++) {
      let columnSum = 0
      for (let j = 0; j < barChartDataset.datasets!.length; j++) {
        columnSum += barChartDataset.datasets![j].data![i] as number
      }
      for (let j = 0; j < barChartDataset.datasets!.length; j++) {
        if (columnSum === 0) {
          barChartDataset.datasets![j].data![i] = 0
          continue
        }
        barChartDataset.datasets![j].data![i] = (barChartDataset.datasets![j].data![i] as number) / columnSum * 100
      }
    }
    return barChartDataset
  }


  processTimeSeriesAbsoluteRevenue(categoryKey: keyof CombinedOrderElementDto, revenueFn: (combinedOrderElementDto: CombinedOrderElementDto) => number): ChartData { //:BarChartDataset
    let revenuePerCategoryOverTime: Map<string, number[]> = new Map<string, number[]>()

    // add all keys from the map of this.timeHelper.axisNameMap
    //let categoryKeys =  this.dtoDataUtils.getMapping(categoryKey as keyof CombinedOrderElementDto)
    let lengthOfAxisNameMap = this.timeRepository.axisNameMap.size


    for (let combinedOrderElement of this.orderRepository.getActiveOrderElements()) {
      let interval = this.timeRepository.toInterval(combinedOrderElement.interval)

      let name = combinedOrderElement[categoryKey as keyof CombinedOrderElementDto] as string
      let revenue = revenueFn(combinedOrderElement)
      if (revenuePerCategoryOverTime.get(name) === undefined) {
        revenuePerCategoryOverTime.set(name, new Array(lengthOfAxisNameMap).fill(0))
      }

      revenuePerCategoryOverTime.get(name)![interval] += revenue
    }


    let datasets = Array.from(revenuePerCategoryOverTime.entries()).map(([key, value]) => (
      {
        label: this.linkedDtoRepository.getName(key, categoryKey as keyof CombinedOrderElementDto),
        data: value,
        backgroundColor: this.colorMapService.getColorAsRGB(this.categoryRevenueRepository.categoryRevenue[categoryKey].findIndex(x => {
          return x.id.toString() === key.toString()
        })!, this.categoryRevenueRepository.categoryRevenue[categoryKey].length),
        borderColor: this.colorMapService.getColorAsRGB(this.categoryRevenueRepository.categoryRevenue[categoryKey].findIndex(x => {
          return x.id.toString() === key.toString()
        })!, this.categoryRevenueRepository.categoryRevenue[categoryKey].length),
        borderWidth: 2
      }))


    let labels = Array.from(this.timeRepository.axisNameMap.values()).map(value => value.name)


    let dataset: ChartData = {
      labels: labels,
      datasets: datasets
    }
    return dataset
  }

  interpolateData(data: ChartData): ChartData {
    if (data === undefined) return data;
    if (data.datasets === undefined) return data;
    for (let date of data.datasets) {
      // @ts-ignore
      date["cubicInterpolationMode"] = "monotone";
      // @ts-ignore
      date['tension'] = 0.4;
    }
    return data;
  }

  removeTensionProperty(data: ChartData): ChartData {
    if (data === undefined) return data;
    if (data.datasets === undefined) return data;
    for (let date of data.datasets) {
      // @ts-ignore
      delete date['tension'];
    }
    return data;
  }


  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }
}
